/**
 * Parent Message Handler
 *
 * Listens for incoming messages from a parent window (web iframe embedding),
 * React Native WebView, or iOS WKWebView. This enables bidirectional
 * communication between the Ramps UI and the hosting application.
 *
 * Supported message types:
 * - INIT_TRANSACTION: Receive multichain transaction parameters from parent
 * - COMMIT_PROCEED: Parent confirms wallet transaction is complete, proceed with commit
 * - COMMIT_ERROR: Parent reports wallet transaction failure
 *
 * Compatible with:
 * - Web: window.postMessage (iframe embedding)
 * - iOS: WebKit message handlers (WKWebView)
 * - Android: ReactNativeWebView.postMessage
 */

import { useCallback, useEffect, useRef } from "react";
import { env } from "@/config/env";
import { processAddressLines } from "@/utils/addressLines";
import { normalizeSenderNames } from "@/utils/senderNames";
import type { SenderProfile } from "../api/types/moneygram";
import { rampsActions } from "../stores/use-ramps-store";

/**
 * Build a SenderProfile from partial user info, filling missing required
 * fields with empty defaults so the type is satisfied.
 * setSenderProfile uses optional chaining and preferStore internally,
 * so empty strings are effectively no-ops.
 */
function buildSenderProfileFromUserInfo(
	ui: Record<string, unknown>,
): SenderProfile {
	// Same normalization as the quote-response path: comma-split lastName,
	// drop duplicated tokens, fit API length limits (parents send raw data).
	const names = normalizeSenderNames({
		firstName: String(ui.firstName || ""),
		middleName: String(ui.middleName || ""),
		lastName: String(ui.lastName || ""),
		secondLastName: String(ui.secondLastName || ""),
	});
	const addressLines = processAddressLines(
		String(ui.address || ui.line1 || ""),
		ui.address2 ? String(ui.address2) : undefined,
	);
	return {
		...names,
		dateOfBirth: String(ui.dateOfBirth || ""),
		birthCountryCode: String(ui.birthCountryCode || ""),
		notificationLanguagePreference: "",
		politicalExposedPerson: false,
		address:
			ui.address || ui.line1 || ui.city || ui.postalCode || ui.countryCode
				? {
						line1: addressLines.line1,
						line2: addressLines.line2 || undefined,
						city: String(ui.city || ""),
						countrySubdivisionCode: String(
							ui.countrySubdivisionCode || ui.state || "",
						),
						countryCode: String(ui.countryCode || "USA"),
						postalCode: String(ui.postalCode || ui.zipCode || ""),
					}
				: {
						line1: "",
						city: "",
						countrySubdivisionCode: "",
						countryCode: "",
						postalCode: "",
					},
		mobilePhone: {
			// Digits only — strip any formatting characters (spaces, dashes, parens)
			number: String(ui.phoneNumber || ui.phone || "").replace(/\D/g, ""),
			// setSenderProfile expects dial code without "+" (it adds the prefix itself)
			countryDialCode: String(
				ui.countryDialCode || ui.phoneCountryCode || "1",
			).replace(/^\+/, ""),
		},
		primaryIdentification: {
			typeCode: String(ui.idType || ""),
			id: String(ui.idNumber || ""),
			issueCountryCode: String(ui.idIssueCountry || ""),
			issueCountrySubdivisionCode: String(ui.idIssueState || ""),
		},
	};
}

// Message types that can be received from parent
export const PARENT_MESSAGE_TYPES = {
	INIT_TRANSACTION: "INIT_TRANSACTION",
	COMMIT_PROCEED: "COMMIT_PROCEED",
	COMMIT_ERROR: "COMMIT_ERROR",
} as const;

export type ParentMessageType =
	(typeof PARENT_MESSAGE_TYPES)[keyof typeof PARENT_MESSAGE_TYPES];

// Message format matching the protocol used by the multi-chain POC
export interface ParentMessage {
	type: ParentMessageType;
	payload: Record<string, unknown>;
	timestamp: number;
}

// Callback types for commit interception
export interface CommitInterceptionCallbacks {
	onCommitProceed?: (payload: { transactionId?: string }) => void;
	onCommitError?: (payload: { error?: string; details?: unknown }) => void;
}

// Callback for INIT_TRANSACTION events (used by ParentMessageInitializer for analytics)
let onInitTransactionCallback:
	| ((payload: Record<string, unknown>) => void)
	| null = null;

export function setOnInitTransactionCallback(
	cb: ((payload: Record<string, unknown>) => void) | null,
): void {
	onInitTransactionCallback = cb;
}

// Singleton to store commit interception callbacks
let commitCallbacks: CommitInterceptionCallbacks = {};

/**
 * Register callbacks for commit interception messages.
 * Called by the review page when it sends a COMMIT_REQUEST and waits for a response.
 */
export function registerCommitCallbacks(
	callbacks: CommitInterceptionCallbacks,
): void {
	commitCallbacks = callbacks;
}

/**
 * Clear commit interception callbacks.
 * Called when commit interception is no longer needed (e.g., page navigation).
 */
export function clearCommitCallbacks(): void {
	commitCallbacks = {};
}

/**
 * Check if the app is running inside an iframe or WebView
 */
export function isEmbedded(): boolean {
	if (typeof window === "undefined") return false;

	// biome-ignore lint/suspicious/noExplicitAny: ReactNativeWebView is dynamically injected
	const hasRNWebView = !!(window as any).ReactNativeWebView;
	const hasIOSWebKit =
		typeof window.webkit !== "undefined" &&
		typeof window.webkit.messageHandlers !== "undefined";
	const hasOpener = !!window.opener;
	let isIframe = false;
	try {
		isIframe = window.parent !== window;
	} catch {
		isIframe = true; // Cross-origin = embedded
	}

	return hasRNWebView || hasIOSWebKit || isIframe || hasOpener;
}

/**
 * Process incoming messages from parent
 */
function handleParentMessage(event: MessageEvent): void {
	// Basic validation - ensure message has the expected shape
	const data = event.data;
	if (!data || typeof data !== "object" || !data.type) return;

	// Only process messages with known types
	if (
		!Object.values(PARENT_MESSAGE_TYPES).includes(
			data.type as ParentMessageType,
		)
	) {
		return;
	}

	if (env.isDevelopment) {
		console.log(`[ParentMessageHandler] Received: ${data.type}`, data.payload);
	}

	switch (data.type) {
		case PARENT_MESSAGE_TYPES.INIT_TRANSACTION: {
			const { wallet_provider, asset_code, operation, lang, userInfo } =
				data.payload || {};

			// Update store with received parameters (same as URL params initialization)
			if (wallet_provider || asset_code || operation || lang) {
				rampsActions.setContextData({
					operation: (operation as "withdraw" | "deposit") || "withdraw",
					lang: (lang as "en" | "es") || "en",
					assetCode: (asset_code as string) || "USDC",
					walletProvider: (wallet_provider as string) || "",
				});

				if (env.isDevelopment) {
					console.log(
						"[ParentMessageHandler] Context data updated from INIT_TRANSACTION:",
						{ wallet_provider, asset_code, operation, lang },
					);
				}
			}

			// Pre-populate user info if provided by the parent app.
			// This supplements the senderProfile from the quote API response.
			// setSenderProfile uses preferStore(), so values from the API
			// or already in the store will NOT be overwritten — only empty
			// fields get filled. This makes it safe to call even when the
			// parent supplies partial data or when no parent sends anything.
			if (userInfo && typeof userInfo === "object") {
				const ui = userInfo as Record<string, unknown>;
				const senderProfile = buildSenderProfileFromUserInfo(ui);
				rampsActions.setSenderProfile(senderProfile);

				if (env.isDevelopment) {
					console.log(
						"[ParentMessageHandler] User info pre-populated from INIT_TRANSACTION:",
						senderProfile,
					);
				}
			}

			// Notify analytics callback
			if (onInitTransactionCallback) {
				onInitTransactionCallback(data.payload || {});
			}
			break;
		}

		case PARENT_MESSAGE_TYPES.COMMIT_PROCEED: {
			if (env.isDevelopment) {
				console.log(
					"[ParentMessageHandler] COMMIT_PROCEED received, triggering commit",
				);
			}
			if (commitCallbacks.onCommitProceed) {
				commitCallbacks.onCommitProceed(
					(data.payload as { transactionId?: string }) || {},
				);
			}
			break;
		}

		case PARENT_MESSAGE_TYPES.COMMIT_ERROR: {
			if (env.isDevelopment) {
				console.log(
					"[ParentMessageHandler] COMMIT_ERROR received:",
					data.payload,
				);
			}
			if (commitCallbacks.onCommitError) {
				commitCallbacks.onCommitError(
					(data.payload as { error?: string; details?: unknown }) || {},
				);
			}
			break;
		}
	}
}

/**
 * React hook to set up parent message listening.
 * Should be called once at the app root level.
 *
 * Listens for:
 * - window.addEventListener('message') for web iframe and React Native WebView
 * - Custom events for iOS WebKit (if needed)
 */
export function useParentMessageListener(): void {
	const initialized = useRef(false);

	useEffect(() => {
		if (initialized.current) return;
		initialized.current = true;

		// Listen for postMessage events (web iframe + React Native WebView)
		window.addEventListener("message", handleParentMessage);

		if (env.isDevelopment) {
			console.log(
				"[ParentMessageHandler] Message listener initialized",
				isEmbedded() ? "(embedded mode detected)" : "(standalone mode)",
			);
		}

		return () => {
			window.removeEventListener("message", handleParentMessage);
			clearCommitCallbacks();
		};
	}, []);
}

/**
 * React hook for commit interception in the review page.
 * Provides a promise-based API for waiting for parent confirmation.
 *
 * Usage in ramps.review.tsx:
 * ```
 * const { waitForCommitConfirmation, isWaitingForCommit } = useCommitInterception();
 *
 * // In handleCommit:
 * if (isEmbedded()) {
 *   sendToWebView('COMMIT_REQUEST', { ... });
 *   const result = await waitForCommitConfirmation();
 *   if (!result.success) { handle error; return; }
 * }
 * // proceed with commit
 * ```
 */
export function useCommitInterception() {
	const resolverRef = useRef<{
		resolve: (value: { success: boolean; error?: string }) => void;
	} | null>(null);
	const isWaitingRef = useRef(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Clean up on unmount
	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
			clearCommitCallbacks();
			if (resolverRef.current) {
				resolverRef.current.resolve({
					success: false,
					error: "Component unmounted",
				});
				resolverRef.current = null;
			}
			isWaitingRef.current = false;
		};
	}, []);

	const waitForCommitConfirmation = useCallback((): Promise<{
		success: boolean;
		error?: string;
	}> => {
		return new Promise((resolve) => {
			isWaitingRef.current = true;
			resolverRef.current = { resolve };

			timerRef.current = setTimeout(() => {
				timerRef.current = null;
				isWaitingRef.current = false;
				resolverRef.current = null;
				clearCommitCallbacks();
				resolve({ success: false, error: "Timeout" });
			}, 30_000);

			registerCommitCallbacks({
				onCommitProceed: () => {
					if (timerRef.current) {
						clearTimeout(timerRef.current);
						timerRef.current = null;
					}
					isWaitingRef.current = false;
					resolverRef.current = null;
					clearCommitCallbacks();
					resolve({ success: true });
				},
				onCommitError: (payload) => {
					if (timerRef.current) {
						clearTimeout(timerRef.current);
						timerRef.current = null;
					}
					isWaitingRef.current = false;
					resolverRef.current = null;
					clearCommitCallbacks();
					resolve({
						success: false,
						error: (payload.error as string) || "Parent reported an error",
					});
				},
			});
		});
	}, []);

	const cancelWait = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		if (resolverRef.current) {
			resolverRef.current.resolve({ success: false, error: "Cancelled" });
			resolverRef.current = null;
		}
		isWaitingRef.current = false;
		clearCommitCallbacks();
	}, []);

	return {
		waitForCommitConfirmation,
		cancelWait,
		isWaitingForCommit: isWaitingRef.current,
	};
}
